import { useEffect, useRef } from 'react';

/**
 * Hook para actualizar el tamaño de gráficos de @ant-design/charts cuando su contenedor cambia de tamaño
 * @param {React.RefObject} chartRef - Referencia al componente de gráfico
 * @param {Array} deps - Dependencias adicionales para forzar el resize
 * @returns {React.RefObject} - Referencia al contenedor que debe asignarse al elemento padre del gráfico
 */
const useChartResize = (chartRef, deps = []) => {
  const containerRef = useRef(null);
  const resizeObserverRef = useRef(null);
  const resizeTimeoutRef = useRef(null);

  useEffect(() => {
    const container = containerRef.current;
    const chartComponent = chartRef.current;

    if (!container || !chartComponent) {
      return;
    }

    // Función para redimensionar el gráfico
    const handleResize = () => {
      // Limpiar timeout anterior para debounce
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }

      resizeTimeoutRef.current = setTimeout(() => {
        try {
          // Para @ant-design/charts 1.4.3
          // El ref expone getChart() que devuelve la instancia interna de G2Plot
          const chart = chartComponent.getChart?.();
          
          if (chart) {
            // Método 1: changeSize - redimensiona al tamaño del contenedor
            if (typeof chart.changeSize === 'function') {
              const { width, height } = container.getBoundingClientRect();
              if (width > 0 && height > 0) {
                chart.changeSize(width, height);
              }
            }
            // Método 2: forceFit - alternativa que ajusta automáticamente
            else if (typeof chart.forceFit === 'function') {
              chart.forceFit();
            }
            // Método 3: render - fuerza un re-render
            else if (typeof chart.render === 'function') {
              chart.render();
            }
          }
        } catch (error) {
          console.warn('Error resizing chart:', error);
        }
      }, 100); // Debounce de 100ms
    };

    // ResizeObserver para detectar cambios en el contenedor
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserverRef.current = new ResizeObserver(() => {
        handleResize();
      });
      resizeObserverRef.current.observe(container);
    }

    // También escuchar resize de la ventana (importante para pantalla completa)
    window.addEventListener('resize', handleResize);

    // Resize inicial
    const initialTimeoutId = setTimeout(handleResize, 150);

    // Cleanup
    return () => {
      clearTimeout(initialTimeoutId);
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
      }
      window.removeEventListener('resize', handleResize);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartRef, ...deps]);

  return containerRef;
};

export default useChartResize;
